import { formatDisplayDate, formatDisplayTime, formatLocalDateTimeTooltip } from "src/utils/dateTime";
import { CircleCheck, CircleAlert, CircleMinus } from "lucide-react";
import { useLayoutEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import type { TLivenessServiceName } from "src/services/livenessData";
import { LIVENESS_COLORS, type LivenessBucket } from "src/services/livenessAggregated";
import { useTranslation } from "react-i18next";

type Bucket = LivenessBucket;
type Props = {
	id: string;
	service: TLivenessServiceName;
	bucket: Bucket;
	anchor: HTMLElement;
	onClose: () => void;
	onEnter: () => void;
	onLeave: () => void;
};

export function LivenessTooltip({ id, service, bucket, anchor, onClose, onEnter, onLeave }: Props) {
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;
		element.setAttribute("popover", "manual");
		element.showPopover();

		const escape = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;
			event.preventDefault();
			event.stopPropagation();
			onClose();
		};
		window.addEventListener("resize", onClose);
		window.addEventListener("scroll", onClose, true);
		document.addEventListener("keydown", escape, true);
		return () => {
			window.removeEventListener("resize", onClose);
			window.removeEventListener("scroll", onClose, true);
			document.removeEventListener("keydown", escape, true);
			element.hidePopover();
		};
	}, [onClose]);

	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;
		const position = () => {
			const rect = anchor.getBoundingClientRect();
			const width = element.offsetWidth;
			const height = element.offsetHeight;
			const center = rect.left + rect.width / 2;
			const left = Math.max(12, Math.min(center - width / 2, window.innerWidth - width - 12));
			const above = rect.top - height - 12 >= 12;
			const top = above ? rect.top - height - 12 : rect.bottom + 12;
			element.style.left = `${left}px`;
			element.style.top = `${Math.max(12, Math.min(top, window.innerHeight - height - 12))}px`;
			element.style.setProperty("--arrow-left", `${Math.max(18, Math.min(center - left, width - 18))}px`);
			element.dataset.side = above ? "above" : "below";
		};
		position();
		const observer = new ResizeObserver(position);
		observer.observe(element);
		return () => observer.disconnect();
	}, [anchor]);

	const Icon = bucket.state === "success" ? CircleCheck : ["failed", "missed", "degraded"].includes(bucket.state) ? CircleAlert : CircleMinus;
	const date = (value: number) => formatDisplayDate(new Date(value), { format: { day: "numeric", month: "short" }, withZone: false });
	const time = (value: number) => formatDisplayTime(new Date(value), { format: { hour: "2-digit", minute: "2-digit" } });
	const dateLabel = date(bucket.from) === date(bucket.to) ? date(bucket.from) : `${date(bucket.from)} – ${date(bucket.to)}`;
	return <Bubble id={id} ref={ref} role="tooltip" onPointerEnter={onEnter} onPointerLeave={onLeave} onClick={event => event.stopPropagation()}>
		<Heading><strong>{service}</strong><Status $state={bucket.state}><Icon size={14} />{t(`liveness.states.${bucket.state}`)}</Status></Heading>
		<Interval><span>{dateLabel}</span><strong>{time(bucket.from)} <span>–</span> {time(bucket.to)}</strong></Interval>
		<LocalInterval>{formatLocalDateTimeTooltip(new Date(bucket.from))}<br />—<br />{formatLocalDateTimeTooltip(new Date(bucket.to))}</LocalInterval>
		<Metrics>
			<div><strong>{bucket.expected.toLocaleString()}</strong><span>{t("liveness.expected")}</span></div>
			<div><strong style={{ color: LIVENESS_COLORS.success }}>{bucket.successful.toLocaleString()}</strong><span>{t("liveness.successful")}</span></div>
			<div><strong style={{ color: LIVENESS_COLORS.failed }}>{bucket.failed.toLocaleString()}</strong><span>{t("liveness.failed")}</span></div>
			<div><strong style={{ color: "#9b7910" }}>{bucket.missed.toLocaleString()}</strong><span>{t("liveness.missed")}</span></div>
		</Metrics><Empty>{t("liveness.openInterval")}</Empty>
	</Bubble>;
}

const appear = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const Bubble = styled.div`
	position: fixed; inset: auto; margin: 0; box-sizing: border-box; width: min(280px, calc(100vw - 24px));
	padding: 16px; border: 1px solid #e3ddd4; border-radius: 13px; background: #fffefc; color: #393630;
	box-shadow: 0 10px 32px #39281824, 0 2px 5px #3928180d; overflow: visible; text-align: left; font-size: 12px;
	&:popover-open { display: block; animation: ${appear} 120ms ease-out both; }
	@media (prefers-reduced-motion: reduce) { &:popover-open { animation: none; } }
	&::after { content: ""; position: absolute; left: var(--arrow-left); width: 9px; height: 9px; background: #fffefc; transform: translateX(-50%) rotate(45deg); }
	&[data-side="above"]::after { bottom: -5px; border-right: 1px solid #e3ddd4; border-bottom: 1px solid #e3ddd4; }
	&[data-side="below"]::after { top: -5px; border-left: 1px solid #e3ddd4; border-top: 1px solid #e3ddd4; }
`;
const Heading = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; > strong { text-transform: capitalize; font-size: 13px; font-weight: 600; }`;
const Status = styled.span<{ $state: Bucket["state"] }>`display: inline-flex; align-items: center; gap: 5px; padding: 4px 7px; border-radius: 6px; font-size: 11px; color: ${props => LIVENESS_COLORS[props.$state]}; background: ${props => props.$state === "failed" ? "#fff0ed" : props.$state === "success" ? "#eef6f0" : "#f3f0eb"};`;
const Interval = styled.div`display: flex; flex-direction: column; gap: 5px; margin-top: 15px; > span { color: #81796f; font-size: 12px; } > strong { font-size: 18px; font-weight: 600; letter-spacing: -.3px; font-variant-numeric: tabular-nums; span { color: #b0a79a; padding: 0 3px; font-weight: 400; } }`;
const Metrics = styled.div`display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; border-top: 1px solid #eee9e1; margin-top: 14px; padding-top: 12px; div { display: flex; flex-direction: column; gap: 4px; } strong { font-size: 18px; font-weight: 600; font-variant-numeric: tabular-nums; } span { color: #81796f; font-size: 11px; }`;
const LocalInterval = styled.div`margin-top: 10px; color: #667085; font-size: 11px; line-height: 1.5; white-space: pre-line; overflow-wrap: anywhere;`;
const Empty = styled.div`margin-top: 14px; padding-top: 12px; border-top: 1px solid #eee9e1; color: #81796f; font-size: 12px; line-height: 1.5;`;
